<template>
  <div class="chart-wrapper">
    <Bar 
      class="custom-class" 
      :style="{ minWidth: computedMinWidth }"
      :chart-options="chartOptions" 
      :chart-data="chartData" 
      :chart-id="chartId"
    />
  </div>
</template>

<script>
import { Bar } from 'vue-chartjs/legacy'
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js'

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

export default {
  components: { Bar },
  props: {
    chartId: { type: String, default: 'bar-chart' },
    dashboardData: { type: Array, default: () => [] },
    // ДОБАВЛЯЕМ НОВЫЙ ПРОПС: передаем текущий формат (day, week, month, quarter)
    currentFormat: { type: String, default: 'day' } 
  },
  data() {
    return {
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        // СУПЕР-ФИШКА ДЛЯ СТЕЙКЕД ГРАФИКОВ: 
        // Показывает в тултипе сразу все 3 значения, если навести на любой кусок столбца
        interaction: {
          mode: 'index',
          intersect: true,
        },
        plugins: {
          legend: {
            display: true,
            labels: { font: { family: 'Inter', size: 14 }, color: '#323c4d' }
          },
tooltip: {
          enabled: false, 
          position: 'nearest',
          external: function(context) {
            const { chart, tooltip } = context;
            let tooltipEl = chart.canvas.parentNode.querySelector('div.custom-chartjs-tooltip');

            // 1. Создаем контейнер, если его нет
            if (!tooltipEl) {
              tooltipEl = document.createElement('div');
              tooltipEl.classList.add('custom-chartjs-tooltip');
              // Стилизуем под ваш скриншот (темно-серый фон, скругления 4px)
              Object.assign(tooltipEl.style, {
                background: 'rgba(50, 50, 50, 0.95)',
                borderRadius: '4px',
                color: '#ffffff',
                opacity: 1,
                pointerEvents: 'none',
                position: 'absolute',
                transition: 'all 0.1s ease',
                padding: '5px 7px',
                fontFamily: 'Inter, sans-serif',
                fontSize: '13px',
                minWidth: '255px',
                zIndex: 999,
                boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
              });
              chart.canvas.parentNode.appendChild(tooltipEl);
            }

            // 2. Прячем тултип, если убрали мышку
            if (tooltip.opacity === 0) {
              tooltipEl.style.opacity = 0;
              return;
            }

            // 3. Собираем HTML как на скриншоте
            const title = tooltip.title[0] || '';
            let total = 0;
            tooltip.dataPoints.forEach(dp => total += dp.raw);

            // Шапка: Дата и Итого
            let innerHtml = `
              <div style="font-weight: 700; font-size: 14px; margin-bottom: 3px;">${title}</div>
              <div style="margin-bottom: 3px;">Итого: ${total.toLocaleString('ru-RU')} пользователей</div>
            `;

            // Строки (Квадратик с белой рамкой + текст)
              tooltip.dataPoints.forEach(dp => {
                const color = dp.dataset.backgroundColor;
                const label = dp.dataset.label;
                const val = dp.raw;
                
                if (val !== null && val !== undefined) {
                  innerHtml += `
                    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; width: 100%;">
                      
                      <div style="display: flex; align-items: center;">
                        <span style="display: inline-block; width: 14px; height: 14px; background: ${color}; border: 1px solid #ffffff; border-radius: 1px; margin-right: 6px;"></span>
                        <span>- ${label}</span>
                      </div>

                      <span style="margin-left: 20px; font-weight: 500;">${val.toLocaleString('ru-RU')}</span>
                      
                    </div>
                  `;
                }
              });

            tooltipEl.innerHTML = innerHtml;

            // 4. УМНОЕ ПОЗИЦИОНИРОВАНИЕ (Решает проблему обрезания)
            tooltipEl.style.opacity = 1;
            
            // Если курсор находится в правой половине графика -> показываем тултип СЛЕВА
            // Если в левой половине -> показываем СПРАВА
            if (tooltip.caretX > chart.width / 2) {
              tooltipEl.style.transform = 'translate(calc(-100% - 15px), -50%)'; 
            } else {
              tooltipEl.style.transform = 'translate(15px, -50%)'; 
            }

            tooltipEl.style.left = tooltip.caretX + 'px';
            tooltipEl.style.top = tooltip.caretY + 'px';
          }
        }
        },
        scales: {
          x: {
            stacked: true, // Включаем группировку по X
            grid: { display: false },
            ticks: {
              font: { family: 'Inter', size: 12 },
              color: '#636e72',
              maxRotation: 45,
              minRotation: 0
            }
          },
          y: {
            stacked: true, // ВАЖНО: Включаем стек по Y (друг на друге)
            beginAtZero: true,
            ticks: { font: { family: 'Inter', size: 12 }, color: '#636e72' }
          }
        }
      }
    }
  },
  computed: {
    // 1. УМНАЯ ШИРИНА: Адаптируемся под формат
    computedMinWidth() {
      const count = this.dashboardData.length;
      
      // Если данных нет, возвращаем 100%
      if (count === 0) return '100%';

      // Рассчитываем, сколько пикселей нужно выделить на 1 столбец в зависимости от формата
      let pixelsPerItem = 0;
      
      switch (this.currentFormat) {
        case 'day':
          pixelsPerItem = 25; // Узкие столбцы, график будет длинным со скроллом
          break;
        case 'week':
          pixelsPerItem = 40; // Средние столбцы
          break;
        case 'month':
          pixelsPerItem = 60; // Широкие столбцы
          break;
        case 'quarter':
          return '100%'; // Кварталы (4 точки в год) всегда умещаем в экран без скролла
      }

      // Если общая ширина получается меньше ширины экрана, оставляем 100% (0)
      // Если больше — возвращаем рассчитанную ширину в пикселях (появится скролл)
      const calculatedWidth = count * pixelsPerItem;
      return count > 15 ? `${calculatedWidth}px` : '100%'; 
    },

    // 2. ДАННЫЕ И ТОЛЩИНА СТОЛБЦОВ
    chartData() {
      if (!this.dashboardData || this.dashboardData.length === 0) {
        return { labels: [], datasets: [] };
      }

      // Настраиваем максимальную толщину столбца в зависимости от формата
      let maxThickness = 30;
      if (this.currentFormat === 'month') maxThickness = 60;
      if (this.currentFormat === 'quarter') maxThickness = 100;

      return {
        labels: this.dashboardData.map(item => item.period),
        datasets: [
          {
            label: 'Незарегистрированные',
            backgroundColor: '#13414E',
            data: this.dashboardData.map(item => (item.basic === 0 || !item.basic) ? null : item.basic),            borderRadius: 4,
            minBarLength: 5, // Если значение = 1, столбец все равно будет высотой 5px
            maxBarThickness: maxThickness // Ограничиваем толщину
          },
          {
            label: 'Зарегистрированные',
            backgroundColor: '#14BF91',
            data: this.dashboardData.map(item => (item.verified === 0 || !item.verified) ? null : item.verified),            minBarLength: 5, // Если значение = 1, столбец все равно будет высотой 5px
            maxBarThickness: maxThickness // Ограничиваем толщину
          },
          {
            label: 'Платящие',
            backgroundColor: '#006D5A',
            data: this.dashboardData.map(item => (item.paying === 0 || !item.paying) ? null : item.paying),
            borderRadius: 4,
            minBarLength: 5, // Если значение = 1, столбец все равно будет высотой 5px
            maxBarThickness: maxThickness // Ограничиваем толщину
          }
        ]
      };
    }
  }
}
</script>

<style scoped>
.chart-wrapper {
  width: 100%;
  overflow-x: auto; /* Скролл появляется только тогда, когда minWidth > 100% */
  overflow-y: hidden;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

.custom-class {
  height: 352px;
  position: relative;
  margin-bottom: 30px;
  margin-top: 10px;
}

/* Красивый скроллбар для Webkit (Chrome, Safari) */
.chart-wrapper::-webkit-scrollbar {
  height: 8px;
}
.chart-wrapper::-webkit-scrollbar-track {
  background: transparent;
}
.chart-wrapper::-webkit-scrollbar-thumb {
  background-color: #cbd5e1;
  border-radius: 4px;
}
</style>